import { BehaviorSubject } from "rxjs";
import { RoleStringEnum } from "../_common/enum";
import { AUTH_URL } from "../_config/config.const";
import { getBody } from "../_helpers/body.helper";
import { handleResponse } from "../_helpers/handleResponse.helper";
import { getHeader, tenantHeaders } from "../_helpers/header.helper";

const currentUser = localStorage.getItem('currentUser') || "";
var currentUserSubject: BehaviorSubject<any>;

var ___userObject = {
    roles: [],
    token: "",
    specalistId: null
}

try {
    ___userObject = JSON.parse(currentUser);
    new BehaviorSubject(___userObject);
}
catch {
    ___userObject = {
        roles: [],
        token: "",
        specalistId: null
    }
}

currentUserSubject = new BehaviorSubject(___userObject);

/** Role strings from API / Identity may use `roles` or `Roles`; values may be "Admin" or "SuperUser". */
export function getRoleStrings(user: any): string[] {
    if (!user) return [];
    const raw = user.roles ?? user.Roles;
    if (!raw) return [];
    if (!Array.isArray(raw)) return [];
    return raw.map((a: any) => (typeof a === "string" ? a : a?.name ?? String(a)));
}

function roleEquals(user: any, role: RoleStringEnum): boolean {
    const target = role.toString().toLowerCase();
    return getRoleStrings(user).some((r) => r.toLowerCase() === target);
}

/** SuperUser in app, or common Identity name "Admin". */
function isAdminUser(user: any): boolean {
    return getRoleStrings(user).some((r) => {
        const n = r.toLowerCase();
        return n === RoleStringEnum.SuperUser.toLowerCase() || n === "admin";
    });
}

export const accountService = {
    login,
    verifyCode,
    logout,
    post,
    put,
    submit(model?: any) { return model?.id ? put(model) : post(model) },
    disable,
    forgotPassword,
    resetPassword,
    currentUser: currentUserSubject.asObservable(),
    get isAdmin() { return isAdminUser(currentUserSubject.value); },
    get isLeader() { return roleEquals(currentUserSubject.value, RoleStringEnum.SocialCareLeader); },
    get isManager() { return roleEquals(currentUserSubject.value, RoleStringEnum.SocialCareManager); },
    get isWorker() { return roleEquals(currentUserSubject.value, RoleStringEnum.SocialCareWorker); },

    get currentUserValue() { return currentUserSubject.value }
};


function login(username: string, password: string, rememberMe: boolean) {
    
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...tenantHeaders() },
        body: JSON.stringify({ username, password, rememberMe })
    };

    return fetch(`${AUTH_URL}/account/authenticate`, requestOptions)
        .then(handleResponse)
        .then((item: any) => {
            const token = item?.token ?? item?.Token;
            if (token) {
                localStorage.setItem('currentUser', JSON.stringify(item));
                currentUserSubject.next(item);
            }
            return item;
        });
}

function verifyCode(username: string, code: string) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, code })
    };

    return fetch(`${AUTH_URL}/account/verifycode`, requestOptions)
        .then(handleResponse)
        .then(user => {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            localStorage.setItem('currentUser', JSON.stringify(user));
            currentUserSubject.next(user);
            return user;
        });
}

function logout() {
    // remove user from local storage to log user out
    localStorage.removeItem('currentUser');
    currentUserSubject.next(null);
    window.location.href = "/login";
}

function resetPassword(email: string, password: string, confirmPassword: string, code: string) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...tenantHeaders() },
        body: JSON.stringify({ email, password, confirmPassword, code })
    };

    return fetch(`${AUTH_URL}/account/resetpassword`, requestOptions)
        .then(handleResponse);
}

function forgotPassword(email: string) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...tenantHeaders() },
        body: JSON.stringify({ email: email })
    };

    return fetch(`${AUTH_URL}/account/forgotpassword`, requestOptions)
        .then(handleResponse);
}

function post(model: any) {
    const requestOptions = { method: 'POST', headers: getHeader(), body: getBody(model) };
    return fetch(`${AUTH_URL}/account/create`, requestOptions).then(handleResponse);
}

function put(model: any) {
    const requestOptions = { method: 'PUT', headers: getHeader(), body: getBody(model) };
    return fetch(`${AUTH_URL}/account/update`, requestOptions).then(handleResponse);
}

function disable(id: any) {
    const requestOptions = { method: 'DELETE', headers: getHeader() };
    return fetch(`${AUTH_URL}/account/disable/?userId=${id}`, requestOptions).then(handleResponse);
}
